import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import Navigation from '../Navigation';
import Title from '../Title';

interface StepProps {
	step: number;
	setStep: (step: number) => void;
	errors: Record<string, string | undefined>;
	isStepValid: boolean;
	isSaving: boolean;
	setIsSaving: (saving: boolean) => void;
}

const Step2 = ({ step, setStep, errors, isStepValid, isSaving, setIsSaving }: StepProps) => {
	const { t } = useTranslation();
	const emailRef = useRef<HTMLInputElement | null>(null);
	const { applicationData, applicationManagement } = useAppContext();
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	useEffect(() => {
		if (step === 2 && emailRef.current && !applicationData.email) {
			emailRef.current.focus();
		}
	}, [step, applicationData.email]);

	if (step !== 2) return null;

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		applicationManagement.update({ [name]: value });
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const { name } = e.target;
		setTouched((prev) => ({ ...prev, [name]: true }));
	};

	const showError = (field: string) => touched[field] && errors?.[field];

	return (
		<div className="flex flex-col justify-center select-none">
			<div className="w-full md:w-10/12 mx-auto">
				<Title
					title={t('application.steps.step4.title')}
					subtitle={t('application.steps.step4.subtitle')}
				/>
				<div className="flex justify-center gap-3 mb-5">
					<div className="w-full md:w-1/2">
						<label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
							{t('application.steps.step4.email')}
							<span className="text-destructive ml-1">*</span>
						</label>
						<Input
							ref={emailRef}
							id="email"
							type="email"
							name="email"
							autoComplete="email"
							value={applicationData.email ?? ''}
							onChange={handleChange}
							onBlur={handleBlur}
							required
							aria-invalid={!!showError('email')}
							aria-describedby={showError('email') ? 'input-feedback-email' : undefined}
							className={cn(showError('email') && 'border-destructive')}
							data-testid="input-email"
						/>
						{showError('email') && (
							<p
								id="input-feedback-email"
								data-testid="input-feedback-email"
								className="mt-1 text-sm text-destructive"
							>
								{errors.email}
							</p>
						)}
					</div>
				</div>
				<div className="flex justify-center gap-3 mb-5">
					<div className="w-full md:w-1/2">
						<label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1">
							{t('application.steps.step4.phone')}
							<span className="text-destructive ml-1">*</span>
						</label>
						<Input
							id="phone"
							type="tel"
							name="phone"
							autoComplete="tel"
							value={applicationData.phone ?? ''}
							onChange={handleChange}
							onBlur={handleBlur}
							maxLength={16}
							required
							aria-invalid={!!showError('phone')}
							aria-describedby={showError('phone') ? 'input-feedback-phone' : undefined}
							className={cn(showError('phone') && 'border-destructive')}
							data-testid="input-phone"
						/>
						{showError('phone') && (
							<p
								id="input-feedback-phone"
								data-testid="input-feedback-phone"
								className="mt-1 text-sm text-destructive"
							>
								{errors.phone}
							</p>
						)}
					</div>
				</div>
			</div>

			<Navigation
				step={step}
				setStep={setStep}
				isStepValid={isStepValid}
				isSaving={isSaving}
				setIsSaving={setIsSaving}
			/>
		</div>
	);
};

export default Step2;
